import { useState, useEffect, useRef } from 'react';
import { useSubjects } from '../../hooks/useSubjects';
import { useIntakes } from '../../hooks/useIntakes';

export default function StudentForm({ 
  initialData = {}, 
  onSubmit, 
  onCancel, 
  loading = false,
  step = 1, // Default to step 1 (student creation)
  createdStudent = null, // For step 2 enrollment
  selectedSubjects = [],
  setSelectedSubjects = () => {},
  selectedIntake = '',
  setSelectedIntake = () => {}
}) {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    address: '',
    date_of_birth: '',
    nric_passport: '',
    contact_no: '',
    status: 'Active',
    payment_type: 'Full',
    enrollment_year: new Date().getFullYear(),

    university_degree: '',
    mqa_recognition: false,
    moodle_access: false,
    remarks: '',
    misc_remarks: '',

    // NEW: RISM Membership Status (frontend-enforced choices)
    membership_status: 'Pending',
  });
  
  const [errors, setErrors] = useState({});
  const { subjects } = useSubjects();
  const { intakes } = useIntakes();
  
  // Use a ref to track if this is the first render
  const firstRender = useRef(true);

  // Determine if we're editing an existing record (has an id)
  const isEdit = Boolean(initialData && initialData.id);
  
  // Initialize form with initial data only on first render or when step changes
  useEffect(() => {
    if (step === 1) {
      setFormData({
        full_name: initialData.full_name || '',
        email: initialData.email || '',
        address: initialData.address || '',
        date_of_birth: initialData.date_of_birth || '',
        nric_passport: initialData.nric_passport || '',
        contact_no: initialData.contact_no || '',
        status: initialData.status || 'Active',
        payment_type: initialData.payment_type || 'Full',
        enrollment_year: initialData.enrollment_year || new Date().getFullYear(),

        // NEW FIELDS (safe defaults)
        university_degree: initialData.university_degree || '',
        mqa_recognition: Boolean(initialData.mqa_recognition) || false,
        moodle_access: Boolean(initialData.moodle_access) || false,
        remarks: initialData.remarks || '',
        misc_remarks: initialData.misc_remarks || '',

        // NEW: membership_status — default 'Pending' if not set
        membership_status: initialData.membership_status || 'Pending',
      });
      setErrors({});
    } else if (step === 2 && firstRender.current === false) {
      // Reset enrollment form when moving to step 2 (but not on first render)
      setSelectedIntake('');
      setSelectedSubjects([]);
    }
    
    // Mark first render as complete
    firstRender.current = false;
  }, [step]); // Only depend on step, not initialData
  
  const validateStudent = () => {
    const newErrors = {};
    
    if (!formData.full_name.trim()) {
      newErrors.full_name = 'Full name is required';
    }
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.enrollment_year) {
      newErrors.enrollment_year = 'Enrollment year is required';
    }
    if (!formData.date_of_birth) {
      newErrors.date_of_birth = 'Date of Birth is required';
    }
    
    if (!formData.nric_passport) {
      newErrors.nric_passport = 'NRIC/Passport is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const validateEnrollment = () => {
    const newErrors = {};
    
    if (!selectedIntake) {
      newErrors.intake = 'Intake is required';
    }
    
    if (selectedSubjects.length === 0) {
      newErrors.subjects = 'At least one subject is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const isValid = step === 1 ? validateStudent() : validateEnrollment();
    if (!isValid) return;
    
    try {
      if (step === 1) {
        await onSubmit(formData);
      } else {
        await onSubmit({
          studentId: createdStudent.id,
          selectedSubjects,
          selectedIntake
        });
      }
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  // Handles both text/select and checkboxes
  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;
    const nextValue = type === 'checkbox' ? checked : value;

    setFormData(prev => ({ ...prev, [name]: nextValue }));
    
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };
  
  const handleSubjectToggle = (subjectId) => {
    setSelectedSubjects(prev => 
      prev.includes(subjectId) 
        ? prev.filter(id => id !== subjectId)
        : [...prev, subjectId]
    );
  };
  
  const handleIntakeChange = (e) => {
    setSelectedIntake(e.target.value);
  };

  // Compute submit button label
  const submitLabel = loading
    ? 'Saving...'
    : step === 1
      ? (isEdit ? 'Update' : 'Create')
      : 'Save';
  
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {step === 1 ? (
        // Student Information Fields
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Full Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Full Name *
            </label>
            <input
              type="text"
              name="full_name"
              value={formData.full_name}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-md ${
                errors.full_name ? 'border-red-500' : 'border-gray-300'
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
              placeholder="Enter full name"
            />
            {errors.full_name && (
              <p className="mt-1 text-sm text-red-600">{errors.full_name}</p>
            )}
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="Active">Active</option>
              <option value="Non-Active">Non-Active</option>
            </select>
          </div>

          {/* NRIC/Passport */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              NRIC/Passport *
            </label>
            <input
              type="text"
              name="nric_passport"
              value={formData.nric_passport}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-md ${
                errors.nric_passport ? 'border-red-500' : 'border-gray-300'
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
              placeholder="Enter NRIC/Passport e.g. 910101011111 without '-'"
            />
            {errors.nric_passport && (
              <p className="mt-1 text-sm text-red-600">{errors.nric_passport}</p>
            )}
          </div>

          {/* Date of Birth */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date of Birth *
            </label>
            <input
              type="date"
              name="date_of_birth"
              value={formData.date_of_birth}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-md ${
                errors.date_of_birth ? 'border-red-500' : 'border-gray-300'
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
            />
            {errors.date_of_birth && (
              <p className="mt-1 text-sm text-red-600">{errors.date_of_birth}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email *
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className={`w-full px-3 py-2 border rounded-md ${
                errors.email ? 'border-red-500' : 'border-gray-300'
              } focus:outline-none focus:ring-2 focus:ring-blue-500`}
              placeholder="student@example.com"
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email}</p>
            )}
          </div>

          {/* Contact No */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Contact No.
            </label>
            <input
              type="tel"
              name="contact_no"
              value={formData.contact_no}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="01XXXXXXXXX"
            />
          </div>

          {/* Address */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Address
            </label>
            <textarea
              name="address"
              value={formData.address}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter full address"
            />
          </div>

          {/* Enrollment Year */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Enrollment Year
            </label>
            <input
              type="number"
              name="enrollment_year"
              value={formData.enrollment_year}
              onChange={handleChange}
              min="2000"
              max="2030"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Enrollment Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Enrollment Type
            </label>
            <select
              name="payment_type"
              value={formData.payment_type}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="Full">Full (10 subjects)</option>
              <option value="Individual">Individual</option>
            </select>
          </div>

          {/* NEW: University Degree */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              University Degree
            </label>
            <input
              type="text"
              name="university_degree"
              value={formData.university_degree}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g. BSc (Hons) Accounting, Universiti Malaya"
            />
          </div>

          {/* NEW: MQA Recognition (checkbox) */}
          <div className="flex items-center space-x-2">
            <input
              id="mqa_recognition"
              type="checkbox"
              name="mqa_recognition"
              checked={formData.mqa_recognition}
              onChange={handleChange}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="mqa_recognition" className="text-sm text-gray-700">
              MQA Recognition
            </label>
          </div>

          {/* NEW: RISM Membership Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              RISM Membership Status
            </label>
            <select
              name="membership_status"
              value={formData.membership_status}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="Pending">Pending</option>
              <option value="Created Account">Created Account</option>
              <option value="Awaiting Secretariat Approval">Awaiting Secretariat Approval</option>
              <option value="Pending Annual Fee">Pending Annual Fee</option>
              <option value="Form F">Form F</option>
              <option value="Completed">Completed</option>
            </select>
          </div>

          {/* NEW: Moodle Access (checkbox) */}
          <div className="flex items-center space-x-2">
            <input
              id="moodle_access"
              type="checkbox"
              name="moodle_access"
              checked={formData.moodle_access}
              onChange={handleChange}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="moodle_access" className="text-sm text-gray-700">
              Moodle Access
            </label>
          </div>

          {/* NEW: Remarks */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Membership & Exam Remarks
            </label>
            <textarea
              name="remarks"
              value={formData.remarks}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Any notes about this student"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Misc. Remarks
            </label>
            <textarea
              name="misc_remarks"
              value={formData.misc_remarks}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Additional miscellaneous notes about this student"
            />
          </div>
        </div>
      ) : (
        // Subject Enrollment Fields
        <div className="space-y-4">
          {createdStudent && (
            <div className="bg-green-50 border border-green-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-green-800">
                    Record for <strong>{createdStudent?.full_name}</strong> updated successfully.
                  </h3>
                </div>
              </div>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Intake *
            </label>
            <select
              value={selectedIntake}
              onChange={handleIntakeChange}
              className={`w-full px-3 py-2 border rounded-md ${errors.intake ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-blue-500`}
            >
              <option value="">Select an intake</option>
              {intakes.map(intake => (
                <option key={intake.id} value={intake.id}>
                  {intake.name} ({intake.start_date} to {intake.end_date})
                </option>
              ))}
            </select>
            {errors.intake && (
              <p className="mt-1 text-sm text-red-600">{errors.intake}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Subjects *
            </label>
            <div className="space-y-2 max-h-60 overflow-y-auto border border-gray-200 rounded-md p-3">
              {subjects.map(subject => (
                <div key={subject.id} className="flex items-center">
                  <input
                    type="checkbox"
                    id={subject.id}
                    checked={selectedSubjects.includes(subject.id)}
                    onChange={() => handleSubjectToggle(subject.id)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor={subject.id} className="ml-2 text-sm text-gray-700">
                    {subject.code} - {subject.name}
                  </label>
                </div>
              ))}
            </div>
            {errors.subjects && (
              <p className="mt-1 text-sm text-red-600">{errors.subjects}</p>
            )}
          </div>
        </div>
      )}
      <div className="flex justify-end space-x-3 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
          disabled={loading}
        >
          {step === 1 ? 'Cancel' : 'Skip for Now'}
        </button>
        <button
          type="submit"
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          disabled={loading || (step === 2 && (!selectedIntake || selectedSubjects.length === 0))}
        >
          {submitLabel}
        </button>
      </div>
    </form>
  );
}
